import { useEffect, useRef, useState } from 'react';
import type { Client, Tranche, UpsertTranche } from '@cer-autofill/shared';
import { apiErrorReason, fetchClients, fetchTranches, saveTranche } from '../api.js';

const blank = (): UpsertTranche => ({
  trancheId: '',
  clientId: '',
  name: '',
  allocatedTarget: 0,
  milestoneDates: { '20': '', '40': '', '60': '', '80': '', '100': '' },
  expectedStillToCome: 0,
  notes: '',
  riskWindowDays: 14,
  active: true,
});

export function TrancheManagementScreen() {
  const [rows, setRows] = useState<Tranche[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [form, setForm] = useState<UpsertTranche>(blank);
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const editorRef = useRef<HTMLElement>(null);
  async function load() {
    try {
      const [tranches, nextClients] = await Promise.all([fetchTranches(), fetchClients(true)]);
      setRows(tranches);
      setClients(nextClients);
    } catch (err) {
      setError(apiErrorReason(err));
    }
  }
  useEffect(() => {
    void load();
  }, []);
  function edit(row: Tranche) {
    const { createdAt: _createdAt, updatedAt: _updatedAt, ...value } = row;
    setForm(value);
    setEditing(true);
    setSaved(false);
    requestAnimationFrame(() =>
      editorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
    );
  }
  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    try {
      await saveTranche(form);
      setForm(blank());
      setEditing(false);
      setSaved(true);
      await load();
    } catch (err) {
      setError(apiErrorReason(err));
    } finally {
      setSaving(false);
    }
  }
  return (
    <>
      <section className="card" ref={editorRef}>
        <h3>{editing ? 'Edit Tranche' : 'Add Tranche'}</h3>
        {error && <p className="error">{error}</p>}
        <form className="tranche-form" onSubmit={(e) => void submit(e)}>
          <label>
            <span>Stable code</span>
            <input
              required
              disabled={editing}
              pattern="[a-z0-9-]+"
              value={form.trancheId}
              onChange={(e) => setForm({ ...form, trancheId: e.target.value.toLowerCase() })}
            />
          </label>
          <label>
            <span>Client</span>
            <select
              required
              value={form.clientId}
              onChange={(e) => setForm({ ...form, clientId: e.target.value })}
            >
              <option value="">Select…</option>
              {clients.map((client) => (
                <option key={client.clientId} value={client.clientId}>
                  {client.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span>Name</span>
            <input
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </label>
          <label>
            <span>Allocated target</span>
            <input
              type="number"
              min="0"
              value={form.allocatedTarget}
              onChange={(e) => setForm({ ...form, allocatedTarget: Number(e.target.value) })}
            />
          </label>
          <label>
            <span>Expected still to come</span>
            <input
              type="number"
              min="0"
              value={form.expectedStillToCome}
              onChange={(e) => setForm({ ...form, expectedStillToCome: Number(e.target.value) })}
            />
          </label>
          <label>
            <span>Risk window (days)</span>
            <input
              type="number"
              min="1"
              value={form.riskWindowDays}
              onChange={(e) => setForm({ ...form, riskWindowDays: Number(e.target.value) })}
            />
          </label>
          <fieldset className="tranche-form__milestones">
            <legend>Milestone dates</legend>
            {(['20', '40', '60', '80', '100'] as const).map((point) => (
              <label key={point}>
                <span>{point}%</span>
                <input
                  required
                  type="date"
                  value={form.milestoneDates[point]}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      milestoneDates: { ...form.milestoneDates, [point]: e.target.value },
                    })
                  }
                />
              </label>
            ))}
          </fieldset>
          <label className="tranche-form__wide">
            <span>Notes</span>
            <textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
            />
          </label>
          <label className="check-label">
            <input
              type="checkbox"
              checked={form.active}
              onChange={(e) => setForm({ ...form, active: e.target.checked })}
            />{' '}
            Active
          </label>
          <div className="tranche-form__actions">
            <button className="primary" disabled={saving}>
              Save tranche
            </button>
            {editing && (
              <button
                type="button"
                onClick={() => {
                  setEditing(false);
                  setForm(blank());
                }}
              >
                Cancel
              </button>
            )}
            {saved && (
              <span className="save-confirmation" role="status">
                Tranche saved
              </span>
            )}
          </div>
        </form>
      </section>
      <div className="tranches-table">
        <div className="tranches-table__row tranches-table__header">
          <div>Tranche</div>
          <div>Client</div>
          <div>Target</div>
          <div>Still to come</div>
          <div>Final milestone</div>
          <div>Status</div>
          <div></div>
        </div>
        {rows.map((row) => (
          <div
            className={`tranches-table__row ${editing && form.trancheId === row.trancheId ? 'is-editing' : ''}`}
            key={row.trancheId}
          >
            <div>
              <strong>{row.name}</strong>
              <small>{row.trancheId}</small>
            </div>
            <div>
              {clients.find((client) => client.clientId === row.clientId)?.shortName ??
                row.clientId}
            </div>
            <div>{row.allocatedTarget.toLocaleString()}</div>
            <div>{row.expectedStillToCome.toLocaleString()}</div>
            <div>{row.milestoneDates['100']}</div>
            <div>{row.active ? 'Active' : 'Inactive'}</div>
            <div>
              <button type="button" onClick={() => edit(row)}>
                Edit
              </button>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}
